import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { motion } from "framer-motion";
import { getERPClient } from "@/lib/erp/client";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { parseLinkSearchResponse, clearLevenshteinCache, type LinkSearchResult } from "@/lib/erp/utils/linkUtils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Search, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { staggerContainer, itemVariants } from "@/components/animation/presets";

type DocTypeSearchAutocompleteProps = {
  /** Required: The DocType to search */
  doctype: string;
  
  /** Current search value */
  value: string;
  
  /** Callback when value changes */
  onChange: (value: string) => void;
  
  /** Placeholder text. If not provided, auto-generates: "Search {doctype}..." */
  placeholder?: string;
  
  /** Whether to show initial suggestions when popover opens (default: true).
   *  Disable for high-volume doctypes like Stock Entry, Journal Entry */
  showInitialSuggestions?: boolean;
  
  /** Minimum characters before searching (default: 2).
   *  Increase for high-volume doctypes */
  minSearchLength?: number;
  
  /** Number of results to fetch (default: 20) */
  pageLength?: number;
  
  /** Optional filters to pass to search_link (Frappe format) */
  filters?: Record<string, any>;
  
  /** Disabled state */
  disabled?: boolean;

  /** Compact mode — smaller input for ClickUp-style toolbar */
  compact?: boolean;

  /** Callback when a dropdown item is explicitly selected (not on every keystroke).
   *  Use this instead of onChange when you need to react only to confirmed selections. */
  onSelect?: (value: string) => void;
};

/**
 * Generic DocType search autocomplete component
 * 
 * Works with any Frappe DocType by using frappe.desk.search.search_link.
 * Automatically respects doctype's search_fields configuration.
 * 
 * Features:
 * - Fully generic (no doctype-specific assumptions)
 * - Cache isolation per doctype
 * - Configurable initial suggestions (disable for high-volume doctypes)
 * - Configurable minimum search length
 * - Optional filters support
 * - Auto-generated placeholders and empty states
 */
export function DocTypeSearchAutocomplete({
  doctype,
  value,
  onChange,
  placeholder,
  showInitialSuggestions = true,
  minSearchLength = 2,
  pageLength = 20,
  filters,
  disabled = false,
  compact = false,
  onSelect: onSelectProp,
}: DocTypeSearchAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevDoctypeRef = useRef<string | null>(null);

  // Request deduplication cache (isolated per doctype)
  const requestCache = useRef(new Map<string, { data: LinkSearchResult[]; timestamp: number }>());
  const CACHE_TTL = 60000; // 60 seconds
  const MAX_CACHE_SIZE = 50;

  // Query cancellation system
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  // Auto-generate placeholder from doctype
  const effectivePlaceholder = placeholder || `Search ${doctype}...`;

  // Auto-generate empty state messages
  const doctypeLower = doctype.toLowerCase();
  const emptyStateText = `No ${doctypeLower}s found`;
  const initialStateText = `Type to search ${doctypeLower}...`;

  // Sync searchTerm with value prop changes
  useEffect(() => {
    setSearchTerm(value || "");
  }, [value]);

  // Clear caches when doctype changes
  useEffect(() => {
    if (prevDoctypeRef.current && prevDoctypeRef.current !== doctype) {
      clearLevenshteinCache();
      requestCache.current.clear();
    }
    prevDoctypeRef.current = doctype;
  }, [doctype]);

  // Debounce search term (250ms as recommended)
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  // Auto-abort on popover close
  useEffect(() => {
    if (!open && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      clearLevenshteinCache();
    };
  }, []);

  // Determine if this is a search query or initial suggestions
  // Empty string always goes to initial suggestions path (wildcard), even with minSearchLength=0
  const effectiveMinLength = minSearchLength ?? 2;
  const trimmedSearch = debouncedSearchTerm.trim();
  const isSearch = trimmedSearch.length > 0 && trimmedSearch.length >= effectiveMinLength;
  const shouldFetchInitial = showInitialSuggestions !== false;

  // Fetch suggestions using search_link
  const { data: results = [], isPending, error } = useQuery<LinkSearchResult[]>({
    queryKey: ["doctype-search", doctype, debouncedSearchTerm.trim() || "initial"],
    queryFn: async ({ signal }) => {
      const client = getERPClient();
      
      // Increment version counter for this request
      const currentVersion = ++requestVersionRef.current;
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Combine signals (React Query + our abort controller)
      const combinedSignal = signal || abortController.signal;
      
      // Check if request was already aborted
      if (combinedSignal.aborted) {
        throw new Error("Request aborted");
      }

      try {
        // Cache key includes doctype for isolation
        const cacheKey = `${doctype}:${debouncedSearchTerm.trim() || "initial"}`;
        const now = Date.now();

        // Check cache first
        const cached = requestCache.current.get(cacheKey);
        if (cached && (now - cached.timestamp) < CACHE_TTL) {
          if (currentVersion === requestVersionRef.current) {
            return cached.data;
          }
          throw new Error("Request superseded");
        }

        let response: any;

        if (isSearch) {
          // Normal search with term
          response = await client.call<any>(
            "frappe.desk.search.search_link",
            {
              doctype,
              txt: debouncedSearchTerm.trim(),
              page_length: pageLength ?? 20,
              filters: filters || undefined,
              signal: combinedSignal,
            }
          );

          // Check version before processing
          if (currentVersion !== requestVersionRef.current) {
            throw new Error("Request superseded");
          }

          // Use shared utility to parse and normalize response with ranking
          const parsed = parseLinkSearchResponse(response, {
            searchInput: debouncedSearchTerm.trim(),
            enableRanking: true,
          });

          // Store in cache
          requestCache.current.set(cacheKey, { data: parsed, timestamp: now });
          
          // Evict oldest entries if cache is full
          if (requestCache.current.size > MAX_CACHE_SIZE) {
            const oldestKey = Array.from(requestCache.current.entries())
              .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
            requestCache.current.delete(oldestKey);
          }

          return parsed;
        } else if (shouldFetchInitial) {
          // Initial suggestions - use search_link with wildcard
          response = await client.call<any>(
            "frappe.desk.search.search_link",
            {
              doctype,
              txt: "%",
              page_length: pageLength ?? 20,
              filters: filters || undefined,
              signal: combinedSignal,
            }
          );

          // Check version before processing
          if (currentVersion !== requestVersionRef.current) {
            throw new Error("Request superseded");
          }

          // Use shared utility to parse and normalize response (no ranking for initial)
          const parsed = parseLinkSearchResponse(response);

          // Store in cache
          requestCache.current.set(cacheKey, { data: parsed, timestamp: now });
          
          // Evict oldest entries if cache is full
          if (requestCache.current.size > MAX_CACHE_SIZE) {
            const oldestKey = Array.from(requestCache.current.entries())
              .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
            requestCache.current.delete(oldestKey);
          }

          return parsed;
        } else {
          // No initial fetch - wait for user to type
          return [];
        }
      } catch (err: any) {
        // Don't log aborted requests as errors
        if (err?.name === "AbortError" || err?.message === "Request aborted" || err?.message === "Request superseded") {
          throw err;
        }
        return [];
      }
    },
    enabled: open, // Fetch when popover is open
    staleTime: 0, // Custom cache handles deduplication
    gcTime: 60000, // Keep in cache for 1 minute
  });

  // Virtual scrolling
  const shouldUseVirtualScroll = results.length > 20;
  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 36, // CommandItem height ~36px
    overscan: 5,
    enabled: shouldUseVirtualScroll && open,
  });

  // Sync selectedIndex with virtual scroll
  useEffect(() => {
    if (shouldUseVirtualScroll && selectedIndex !== -1 && selectedIndex < results.length) {
      virtualizer.scrollToIndex(selectedIndex, {
        align: "auto",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, shouldUseVirtualScroll, virtualizer, results.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex !== -1 && results[selectedIndex]) {
        e.preventDefault();
        handleSelect(results[selectedIndex].value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, results, selectedIndex]);

  const handleSelect = (selectedValue: string) => {
    setSearchTerm(selectedValue);
    onChange(selectedValue); // Update searchQuery in parent
    onSelectProp?.(selectedValue); // Notify parent of confirmed selection
    setOpen(false);
    setSelectedIndex(-1);
  };

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Search className={cn(
              "absolute top-1/2 -translate-y-1/2 text-muted-foreground",
              compact ? "left-2.5 w-4 h-4" : "left-3 w-4 h-4",
            )} />
            <Input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerm(newValue);
                setSelectedIndex(-1);
                onChange(newValue); // Update searchQuery in parent immediately
                setOpen(true); // Keep popover open when typing
              }}
              onFocus={() => {
                setOpen(true); // Show initial suggestions on focus (if enabled)
              }}
              onBlur={(e) => {
                // Don't close if clicking within the popover portal
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget?.closest('[data-radix-popper-content-wrapper]') || relatedTarget?.closest('[cmdk-list]')) {
                  e.preventDefault();
                }
              }}
              placeholder={effectivePlaceholder}
              disabled={disabled}
              className={compact
                ? "h-8 w-[200px] text-sm rounded-full bg-accent/40 border-0 pl-8 pr-3 transition-all duration-150 focus:w-[280px] focus:bg-white dark:focus:bg-gray-900 focus:border focus:border-border focus:ring-1 focus:ring-primary/20 focus:ring-offset-0 placeholder:text-muted-foreground/70"
                : "h-14 w-full text-base rounded-full bg-white dark:bg-gray-900 border border-border pl-10 transition-all duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] focus:ring-2 focus:ring-primary/20 focus:ring-offset-0"
              }
            />
          </div>
        </PopoverAnchor>
        {open && (
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl backdrop-saturate-150 border border-white/40 dark:border-gray-700/40 shadow-[rgba(255,255,255,0.2)_0px_0px_0px_1px_inset,rgba(255,255,255,0.3)_0px_0px_20px,rgba(0,0,0,0.03)_0px_1px_4px,rgba(0,0,0,0.05)_0px_4px_12px,rgba(0,0,0,0.03)_0px_8px_24px,rgba(0,0,0,0.016)_0px_16px_48px]"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => {
                // Prevent DismissableLayer from closing when the click is on our anchor input
                // (the same click that triggered onFocus → setOpen(true))
                if (inputRef.current?.contains(e.target as Node)) {
                  e.preventDefault();
                }
              }}
              onFocusOutside={(e) => {
                if (inputRef.current?.contains(e.target as Node)) {
                  e.preventDefault();
                }
              }}
            >
                <Command shouldFilter={false} className="bg-transparent">
                  <CommandList
                    ref={listRef}
                    className={cn(
                      shouldUseVirtualScroll ? "max-h-[300px] overflow-auto" : "",
                      "min-h-[44px]",
                    )}
                  >
                    {isPending && (
                      <CommandEmpty>Loading...</CommandEmpty>
                    )}
                    {!isPending && error && (
                      <CommandEmpty>Error loading results</CommandEmpty>
                    )}
                    {!isPending && !error && results.length === 0 && trimmedSearch.length > 0 && (
                      <CommandEmpty>{emptyStateText}</CommandEmpty>
                    )}
                    {!isPending && !error && results.length === 0 && trimmedSearch.length === 0 && !shouldFetchInitial && (
                      <CommandEmpty>{initialStateText}</CommandEmpty>
                    )}
                    {!isPending && !error && results.length > 0 && (
                        <motion.div
                          key="results"
                          variants={!shouldUseVirtualScroll ? staggerContainer : undefined}
                          initial="initial"
                          animate="animate"
                        >
                          <CommandGroup>
                            {shouldUseVirtualScroll ? (
                              // Virtual scrolling for >20 results - NO animation to avoid conflicts
                              <div
                                style={{
                                  height: `${virtualizer.getTotalSize()}px`,
                                  width: "100%",
                                  position: "relative",
                                }}
                              >
                                {virtualizer.getVirtualItems().map((virtualItem) => {
                                  const result = results[virtualItem.index];
                                  const displayLabel = result.label || result.description || result.value || "";
                                  const resultValue = result.value || "";
                                  const isSelected = selectedIndex === virtualItem.index;
                                  return (
                                    <CommandItem
                                      key={`${resultValue}-${debouncedSearchTerm}-${virtualItem.index}`}
                                      value={resultValue}
                                      onSelect={() => handleSelect(resultValue)}
                                      className={cn(
                                        "transition-all duration-120 ease-[cubic-bezier(0.16,1,0.3,1)] rounded mx-1",
                                        isSelected && "bg-accent/70 dark:bg-accent/50"
                                      )}
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: `${virtualItem.size}px`,
                                        transform: `translateY(${virtualItem.start}px)`,
                                      }}
                                    >
                                      <motion.div
                                        animate={isSelected ? { scale: 0.98 } : { scale: 1 }}
                                        transition={{ duration: 0.08 }}
                                        className="flex items-center w-full"
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 transition-opacity duration-120",
                                            value === resultValue ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                        <span title={resultValue}>{displayLabel}</span>
                                      </motion.div>
                                    </CommandItem>
                                  );
                                })}
                              </div>
                            ) : (
                              // Regular rendering for ≤20 results - WITH stagger animation
                              results.map((result, index) => {
                                const displayLabel = result.label || result.description || result.value || "";
                                const resultValue = result.value || "";
                                const isSelected = selectedIndex === index;
                                return (
                                  <motion.div
                                    key={`${resultValue}-${debouncedSearchTerm}-${index}`}
                                    variants={itemVariants}
                                  >
                                    <CommandItem
                                      value={resultValue}
                                      onSelect={() => handleSelect(resultValue)}
                                      className={cn(
                                        "transition-all duration-120 ease-[cubic-bezier(0.16,1,0.3,1)] rounded mx-1",
                                        isSelected && "bg-accent/70 dark:bg-accent/50"
                                      )}
                                    >
                                      <motion.div
                                        animate={isSelected ? { scale: 0.98 } : { scale: 1 }}
                                        transition={{ duration: 0.08 }}
                                        className="flex items-center w-full"
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 transition-opacity duration-120",
                                            value === resultValue ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                        <span title={resultValue}>{displayLabel}</span>
                                      </motion.div>
                                    </CommandItem>
                                  </motion.div>
                                );
                              })
                            )}
                          </CommandGroup>
                        </motion.div>
                    )}
                  </CommandList>
                </Command>
            </PopoverContent>
          )}
      </Popover>
    </div>
  );
}

